// ghostTaunts.ts — GHOST DUELS (Slice D) ghost-identity captions. A raced ghost is a silent replay
// entity (ghostRacer.ts); this gives it a VOICE without any per-ghost audio: small plated captions
// keyed by race moment, the ghost's name woven in. Captions-only by design — VO exists for BYT3 the
// gatekeeper, never for an arbitrary player's ghost (no per-ghost recordings, no moderation surface).
//
// The pick is name-hash-seeded (FNV-1a over name+moment) so a given ghost always says the SAME line at
// the SAME beat — stable identity, deterministic, no RNG needed. All lines are ORIGINAL arcade
// trash-talk (no IP), name-agnostic, and safe with any sanitized ≤12-char display name.
//
// Renderer-only, framework-light: one Phaser text plate that shows even under mute (accessibility) and
// motion-gates its own pop/float. It imports nothing from ghostRacer (which imports THIS) — no cycle.
import Phaser from 'phaser';
import { FONT } from './keys';
import { motionAllowed, scaledFont } from './settings';
import { requestCaption, CAPTION_PRIORITY } from './captionSlot';

/** The race beats a ghost can talk at (edge-triggered by ghostRacer). */
export type TauntMoment = 'raceStart' | 'ghostLeads' | 'playerPasses' | 'ghostFinishes';

/**
 * Template pools per moment. `{name}` is replaced with the ghost's display name. 3–4 ORIGINAL lines
 * each; the name-hash picks one deterministically so a ghost's persona is stable across a run.
 */
const TAUNTS: Record<TauntMoment, readonly string[]> = {
  // Race start — the echo powers up / locks on. Ambient menace, no quote.
  raceStart: ['{name} is watching…', "{name}'s ghost locks on.", '{name} fades in — race on.', '{name} is already moving.'],
  // Ghost pulls ahead — a taunt.
  ghostLeads: ['{name}: “Too slow.”', '{name}: “Keep up.”', '{name}: “This is my line.”', '{name}: “Eat the echo.”'],
  // Player overtakes — rattled surprise.
  playerPasses: ['{name}: “…no way.”', '{name}: “How?!”', '{name}: “Not for long.”', '{name}: “Lucky route.”'],
  // Ghost reaches the end of its track — throws down the marker.
  ghostFinishes: ['{name}: “Beat that.”', '{name}: “That’s the mark.”', '{name}: “Your turn.”', '{name}: “Catch me.”'],
};

/** FNV-1a (32-bit) over a string → stable non-negative index seed. No Math.random: identity is fixed. */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Pick this ghost's line for a moment — deterministic per (name, moment). */
export function pickTaunt(name: string, moment: TauntMoment): string {
  const pool = TAUNTS[moment];
  const idx = fnv1a(`${name}|${moment}`) % pool.length;
  return pool[idx].replace('{name}', name);
}

/** Lifetime of a ghost taunt card (fade-in + hold + fade-out) before it self-destroys. */
const GHOST_CAPTION_LIFE_MS = 1950;

/**
 * Spawn a small plated ghost caption at (x, y), MIRRORING the rail the ghost chip sits on: when the
 * chip is on the LEFT rail (landscape stage) the plate grows RIGHT (origin 0) so it never clips off the
 * left viewport edge; when the chip is top-right (portrait) it grows LEFT (origin 1), hugging the chip's
 * right edge as before. The plate is then CLAMPED inside the stage either way (defence in depth) so a
 * long ghost name can never spill past a viewport edge. Violet-on-dark to read as the GHOST speaking,
 * distinct from BYT3's pink plate.
 *
 * S3b gap 2: routed through the ONE shared caption slot (requestCaption, ghost priority) so a ghost
 * taunt and a BYT3 bark can never stack two bubbles in the corner — the slot serialises them and
 * drops the loser rather than overlapping. Shows even under mute (accessibility); the fade/float is
 * motion-gated. Self-destructs, leaking nothing.
 */
export function spawnGhostCaption(scene: Phaser.Scene, x: number, y: number, line: string, accent: string): void {
  requestCaption(
    scene,
    (sc) => {
      // The stage design width == the Scale.FIT logical width (portrait 720 / landscape 1280). A chip on
      // the left half of the stage means the LEFT rail → grow the caption rightward off that edge.
      const stageW = sc.scale.width;
      const growRight = x < stageW / 2;
      const plate = sc.add
        .text(0, 0, line, {
          fontFamily: FONT.display,
          fontSize: `${Math.round(scaledFont(13, 11))}px`,
          fontStyle: 'bold italic',
          color: accent,
          backgroundColor: 'rgba(12,10,24,0.88)',
          padding: { x: 7, y: 4 },
          stroke: '#0a0814',
          strokeThickness: 2,
        })
        .setOrigin(growRight ? 0 : 1, 0); // left rail grows right from x; right rail grows left to x
      const card = sc.add.container(x, y, [plate]).setDepth(8990).setAlpha(0);
      // Clamp the plate inside the stage (≥8px margins) whichever way it grows — a long name never clips.
      const margin = 8;
      const w = plate.width;
      const plateLeft = growRight ? x : x - w;
      const plateRight = growRight ? x + w : x;
      if (plateLeft < margin) card.x += margin - plateLeft;
      else if (plateRight > stageW - margin) card.x -= plateRight - (stageW - margin);
      sc.tweens.add({ targets: card, alpha: { from: 0, to: 1 }, duration: 150, yoyo: true, hold: 1500 });
      if (motionAllowed()) sc.tweens.add({ targets: card, y: y + 12, duration: 1900, ease: 'Sine.easeOut' });
      sc.time.delayedCall(GHOST_CAPTION_LIFE_MS, () => card.destroy());
      return GHOST_CAPTION_LIFE_MS;
    },
    CAPTION_PRIORITY.ghost,
  );
}
